import React, { useState } from 'react'
import { BellOutlined, LogoutOutlined, MenuFoldOutlined, MenuUnfoldOutlined, ReloadOutlined, UserOutlined } from '@ant-design/icons'
import { Badge, Button, Dropdown, Flex, Layout, Spin, theme, Tooltip, Typography } from 'antd'
import { Link, useNavigate } from 'react-router'
import { ImpersonateUserModal } from '@/components/ImpersonateUserModel.tsx'
import Title from 'antd/es/typography/Title'
import { NavMenu } from './NavMenu.tsx'
import { useLazyGetPermissionsQuery, useLazyGetRolesQuery } from '@/features/auth/auth.api.ts'
import { useGetUserMessagesQuery } from '@/features/user-messages/user-messages.api.ts'
import { useAuth } from '@/hooks/useAuth.ts'
import { routes } from '@/router/routes.tsx'
import { FinancePortalEnv } from '@/features/auth/auth.def.ts'
import { featureFlags } from '@/config/feature-flags.ts'
import { cn } from '@/utils/style.ts'
import { AppName } from '@/types/misc.def.ts'

const { Header, Sider, Content } = Layout

const envBadges: Record<string, { title: string, className: string }> = {
  [FinancePortalEnv.Dev]: { title: 'DEV', className: 'bg-white' },
  [FinancePortalEnv.Uat]: { title: 'UAT', className: 'text-white bg-amber-500' },
  [FinancePortalEnv.Prod]: { title: 'PROD', className: 'text-white bg-red-500' },
}

export const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isImpersonated, impersonatedUsername, checkHasAllPermissions } = useAuth()
  const [collapsed, setCollapsed] = useState(false)
  const navigate = useNavigate()
  const [permissionsQuery, { isLoading: isPermissionsLoading }] = useLazyGetPermissionsQuery()
  const [rolesQuery, { isLoading: isRolesLoading }] = useLazyGetRolesQuery()
  const { data: userMessages = [] } = useGetUserMessagesQuery(undefined, {
    pollingInterval: 60_000,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  })
  const envBadge = envBadges[import.meta.env.VITE_FINANCE_PORTAL_ENV] ?? envBadges[FinancePortalEnv.Dev]
  const isLoading = isPermissionsLoading || isRolesLoading
  const unreadMessageCount = userMessages.filter(message => !message.isRead).length
  const canImpersonate = featureFlags.impersonateUser
    && checkHasAllPermissions([{ moduleName: 'assume-identity', functionName: 'assume-user' }])

  const {
    token: { colorBgContainer },
  } = theme.useToken()

  const refreshPermissions = async () => {
    await permissionsQuery()
    await rolesQuery()
    location.reload()
  }

  return (
    <div
      id="app-layout"
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'row',
        position: 'relative',
        flexWrap: 'nowrap',
      }}
    >
      <Layout>
        <Spin fullscreen spinning={isLoading} />
        <Header className="relative bg-slate-700 px-4 py-0 h-12 flex items-center justify-between">
          <Flex gap="small">
            <Button
              type="text"
              htmlType="button"
              className="bg-white"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(prev => !prev)}
            />
          </Flex>
          <Link to="/" className="absolute left-1/2 -translate-x-1/2">
            <Flex
              gap="small"
              align="center"
            >
              <Title level={4} className="m-0 text-white">{AppName}</Title>
              <Typography.Text
                className={cn('m-0 font-bold px-1 rounded-md', envBadge.className)}
              >{envBadge.title}</Typography.Text>
            </Flex>
          </Link>
          <Flex gap="small">
            {/*{user?.roles?.includes(HcRole.Developer) ?*/}
            {/*  <Button onClick={() => navigator.clipboard.writeText(user.token)}>Copy Token</Button> : null}*/}
            {/*{user?.roles?.includes(HcRole.Developer) ?*/}
            {/*  <Link className="leading-none" to={import.meta.env.VITE_OKTA_LOGOUT_URL} target="_blank">*/}
            {/*    <Button danger>*/}
            {/*      Okta Logout*/}
            {/*    </Button>*/}
            {/*  </Link>*/}
            {/*  : null}*/}
            {canImpersonate ? <ImpersonateUserModal /> : null}
            <Tooltip title="Notifications">
              <Badge count={unreadMessageCount} showZero={false} size="small" className="relative z-10">
                <Button
                  type="text"
                  htmlType="button"
                  className="bg-white"
                  icon={<BellOutlined />}
                  aria-label="Open notifications"
                  onClick={() => navigate(routes.notifications)}
                />
              </Badge>
            </Tooltip>
            <Dropdown
              menu={{
                items: [
                  {
                    label: 'Refresh Permissions',
                    key: 'refreshPermissions',
                    icon: <ReloadOutlined />,
                    onClick: refreshPermissions,
                  },
                  {
                    label: 'Logout',
                    key: 'logout',
                    icon: <LogoutOutlined />,
                    onClick: () => navigate(routes.logout),
                  },
                ],
              }}
            >
              <Button
                type="text"
                htmlType="button"
                className="bg-white"
                icon={<UserOutlined />}
              />
            </Dropdown>
          </Flex>
        </Header>
        <Layout className="relative">
          <RootLayoutSider collapsed={collapsed} />
          {!canImpersonate && isImpersonated ? (
            <div className="bg-red-500 text-center absolute top-0 z-50 p-1 w-fit left-1/2 -translate-x-1/2">
              <Typography.Text className="text-md text-white font-bold">
                You are now impersonated as: {impersonatedUsername}
              </Typography.Text>
            </div>
          ) : null}
          <Content
            style={{
              overflow: 'auto',
              height: '100%',
              background: colorBgContainer,
            }}
          >
            {children}
          </Content>
        </Layout>
      </Layout>
    </div>
  )
}

const RootLayoutSider: React.FC<{ collapsed: boolean }> = ({ collapsed }) => {
  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      theme="light"
      width={250}
      className="overflow-hidden bg-slate-900"
    >
      <NavMenu />
    </Sider>
  )
}